import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  Trash2,
  RefreshCcw,
  MoreHorizontal,
  ShieldCheck,
  Edit,
} from "lucide-react";
import { User } from "@/types/user";
import { format } from "date-fns";
import { formatRoleName } from "@/utils";

interface UserTableProps {
  users: User[];
  onDeleteUser: (userId: string) => void;
  onEditUser?: (user: User) => void;
}

export const UserTable: React.FC<UserTableProps> = ({
  users,
  onDeleteUser,
  onEditUser,
}) => {
  const [mfaSetupOpen, setMfaSetupOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const getInitials = (name: string | undefined | null) => {
    if (!name) return "";
    return name
      .split(" ")
      .map((part) => part[0])
      .join("")
      .toUpperCase();
  };

  const getRoleBadgeStyle = (role: string) => {
    switch (role) {
      case "admin":
        return "bg-red-100 text-red-800 hover:bg-red-100";
      case "manager":
        return "bg-blue-100 text-blue-800 hover:bg-blue-100";
      default:
        return "bg-gray-100 text-gray-800 hover:bg-gray-100";
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), "MMM d, yyyy HH:mm a");
    } catch (error) {
      return "Invalid date";
    }
  };

  const formatLastLogin = (dateString?: string) => {
    if (!dateString) return "";
    try {
      return format(new Date(dateString), "MMM d, yyyy HH:mm a");
    } catch (error) {
      return "Invalid date";
    }
  };

  const handleEnableMFA = (user: User) => {
    setSelectedUser(user);
    setMfaSetupOpen(true);
  };

  const trimRole = (role: string, maxLength = 12) => {
    if (!role) return "";
    return role.length > maxLength ? role.slice(0, maxLength) + "..." : role;
  };

  return (
    <div className="w-full overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name/Role</TableHead>
            <TableHead>Username</TableHead>
            <TableHead>Created</TableHead>
            <TableHead>Last Login</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                No users found
              </TableCell>
            </TableRow>
          ) : (
            users.map((user) => (
              <TableRow key={user.id} className="h-16">
                <TableCell className="flex items-center space-x-3">
                  <Avatar>
                    <AvatarImage src={user.avatarUrl} />
                    <AvatarFallback>{getInitials(user.name)}</AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">{user.name}</p>
                    <p className="text-xs text-gray-500">
                      {formatRoleName(
                        user.role.charAt(0).toUpperCase() + user.role.slice(1)
                      )}
                    </p>
                  </div>
                </TableCell>
                <TableCell>{user.username}</TableCell>
                <TableCell>{formatDate(user.createdAt)}</TableCell>
                <TableCell>{formatLastLogin(user.lastLogin)}</TableCell>
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <MoreHorizontal size={16} />
                        <span className="sr-only">Open menu</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {onEditUser && (
                        <DropdownMenuItem
                          onClick={() => onEditUser(user)}
                          className="flex items-center cursor-pointer"
                        >
                          <Edit size={16} className="mr-2" /> Edit User
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem
                        onClick={() => onDeleteUser(user.id)}
                        className="flex items-center text-red-600 cursor-pointer hover:text-red-600 focus:text-red-600"
                      >
                        <Trash2 size={16} className="mr-2" /> Delete User
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
};
